/**
 * Wrap numeric+unit metric tokens in `<span class="badge-solid">` so the
 * locked design's signature numeric pill styling applies to model output.
 *
 * Operates on the HTML string produced by `marked` *before* DOMPurify
 * sanitization. Skips content inside `<pre>`/`<code>` blocks (we do not
 * want to retroactively re-style numbers inside source code or tables of
 * raw data the LLM emitted as code) and inside existing `<span>` tags
 * (avoids double-wrapping if upstream ever adds badges itself).
 *
 * Streaming-safe: pure string transform, microsecond-fast on chat-turn-
 * sized inputs. Re-runs on every source change inside MarkdownView's memo.
 *
 * Unit allowlist is intentionally conservative — known metric units only.
 * Adding "B" / "K" / "M" suffixes would risk wrapping plain letters that
 * follow numbers ("3 B-trees"), so those forms require no-space attach
 * (e.g. `30B`, `100K`) and a vetted suffix set.
 */
const SENTINEL = " ";

// Units that take an optional space before them (e.g. "546 GB/s", "350W").
// Order matters: longer units before shorter prefixes (GB/s before GB) so
// the regex doesn't truncate the match.
const SPACED_UNITS = [
  "GB/s",
  "MB/s",
  "TB/s",
  "TFLOPS",
  "TFLOPs",
  "GFLOPS",
  "TOPS",
  "GHz",
  "MHz",
  "kW",
  "W",
  "GB",
  "MB",
  "TB",
  "ms",
  "ns",
  "tok/s",
  "req/s",
];

// Units that ALWAYS attach with no space (parameter counts, multipliers,
// percentages). `B`/`M`/`K` are restricted to this form to avoid wrapping
// stray capital letters that happen to follow numbers.
const ATTACHED_UNITS = ["B", "M", "K", "×", "x", "%"];

const NUMBER = "~?\\d+(?:[.,]\\d+)?(?:[\\u2013\\u2014\\-]\\d+(?:[.,]\\d+)?)?";

// Boundary that allows a leading `~` while still rejecting mid-word
// matches (avoids `abc100GB` triggering inside a stray word).
const LEFT_BOUNDARY = "(?<![\\w.])";

const METRIC_RE = new RegExp(
  // ── spaced form: NUMBER + optional space + UNIT, word-bounded
  `(?:${LEFT_BOUNDARY}${NUMBER}\\s?(?:${SPACED_UNITS.join("|")})\\b)` +
    // ── attached form: NUMBER immediately followed by UNIT (no space)
    `|(?:${LEFT_BOUNDARY}${NUMBER}(?:${ATTACHED_UNITS.join("|")}))`,
  "g",
);

function wrapMetricsInText(text: string): string {
  return text.replace(
    METRIC_RE,
    (m) => `<span class="badge-solid">${m}</span>`,
  );
}

/**
 * Wrap metric tokens in HTML produced by marked, skipping protected
 * regions (`<pre>...</pre>`, `<code>...</code>`, existing `<span>`s,
 * and tag bodies themselves).
 */
export function wrapNumericBadges(html: string): string {
  if (!html) return html;

  const protectedRegions: string[] = [];
  const stash = (m: string): string => {
    protectedRegions.push(m);
    return `${SENTINEL}${protectedRegions.length - 1}${SENTINEL}`;
  };

  let masked = html;
  // Protect code blocks (pre wraps code; doing both covers inline + block).
  masked = masked.replace(/<pre[\s\S]*?<\/pre>/gi, stash);
  masked = masked.replace(/<code[^>]*>[\s\S]*?<\/code>/gi, stash);
  // Protect existing spans so we never wrap an already-wrapped metric.
  masked = masked.replace(/<span[^>]*>[\s\S]*?<\/span>/gi, stash);

  // Wrap metrics in remaining text (only the bits between tags, never
  // inside tag bodies — `>...<` cleanly excludes attribute strings).
  masked = masked.replace(
    />([^<]+)</g,
    (_, text: string) => `>${wrapMetricsInText(text)}<`,
  );

  // Restore protected regions in stash order.
  return masked.replace(
    new RegExp(`${SENTINEL}(\\d+)${SENTINEL}`, "g"),
    (_, idx: string) => protectedRegions[Number(idx)] ?? "",
  );
}
